'use client'

import s from './Marquee.module.scss'
import { useMemo, useRef, useState, useLayoutEffect, useEffect } from 'react'
import { motion, useAnimationFrame, useMotionValue } from 'framer-motion'
import LazyImage from '@/components/Common/LazyImage'
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion'

type Props = { data: any }

const SPEED = 60  // px/s
const COPIES = 3  // 1 original + 2 clones

export default function Marquee({ data }: Props) {
  const images = useMemo(() => data?.images ?? [], [data])
  const prefersReducedMotion = usePrefersReducedMotion()

  const x = useMotionValue(0)
  const outerRef = useRef<HTMLDivElement | null>(null)
  const innerRef = useRef<HTMLDivElement | null>(null)
  const setRef  = useRef<HTMLDivElement | null>(null)  // original set only

  const [isDragging, setIsDragging]       = useState(false)
  const [isPaused,   setIsPaused]         = useState(false)
  const [setWidth,   setSetWidth]         = useState(0)
  const [clonesReady, setClonesReady]     = useState(false)

  let pointerMoved = false

  // Activate clones client-side only (never runs on server / during SSR)
  useEffect(() => {
    setClonesReady(true)
  }, [])

  // Horizontal wheel scroll
  useEffect(() => {
    const el = outerRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      if (!setWidth) return
      const horiz = Math.abs(e.deltaX) > Math.abs(e.deltaY)
        ? e.deltaX
        : e.shiftKey ? e.deltaY : 0
      if (!horiz) return
      e.preventDefault()
      x.set(wrapX(x.get() - horiz))
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel as any)
  }, [setWidth])

  // Measure the original set's width directly (no division needed)
  useLayoutEffect(() => {
    const el = setRef.current
    if (!el) return
    const measure = () => setSetWidth(el.scrollWidth)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [images.length])

  const wrapX = (val: number) => {
    if (!setWidth) return val
    while (val <= -setWidth) val += setWidth
    while (val >= 0)          val -= setWidth
    return val
  }

  // Don't animate until clones are present (avoids jump on first render)
  useAnimationFrame((_, delta) => {
    if (prefersReducedMotion || !clonesReady || isDragging || isPaused || setWidth === 0) return
    x.set(wrapX(x.get() - (SPEED * delta) / 1000))
  })

  const renderImages = () =>
    images.map((image: any, index: number) => (
      <div key={index} className={s.imageWrapper}>
        <div className={s.imageInner} draggable={false}>
          <LazyImage
            src={image?.imageUrl}
            alt={image?.alt}
            width={image?.metadata?.dimensions?.width || 800}
            height={image?.metadata?.dimensions?.height || 600}
            filename={image.filename}
          />
        </div>
      </div>
    ))

  return (
    <div ref={outerRef} className={s.marqueeOuter}>
      <motion.div
        ref={innerRef}
        className={s.marqueeInner}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        style={{
          x,
          willChange: 'transform',
          touchAction: 'pan-y',
          cursor: isDragging ? 'grabbing' : 'grab',
        }}
        drag="x"
        dragElastic={0.06}
        dragMomentum={true}
        onDrag={() => x.set(wrapX(x.get()))}
        onDragStart={() => { setIsDragging(true);  setIsPaused(true)  }}
        onDragEnd={()  => { setIsDragging(false); setIsPaused(false); x.set(wrapX(x.get())) }}
        onPointerDown={() => { pointerMoved = false; setIsPaused(true)  }}
        onPointerMove={() => { pointerMoved = true }}
        onPointerUp={() => { if (!pointerMoved) setIsPaused(true); else setIsPaused(false) }}
      >
        {/* Original set — rendered in SSR HTML, visible to crawlers */}
        <div ref={setRef} className={s.marqueeSet}>
          {renderImages()}
        </div>

        {/* Clones — added client-side only, hidden from assistive tech */}
        {clonesReady && Array.from({ length: COPIES - 1 }).map((_, i) => (
          <div key={i} className={s.marqueeSet} aria-hidden="true">
            {renderImages()}
          </div>
        ))}
      </motion.div>
    </div>
  )
}
